import React, { useMemo, useState } from "react";

type ReportTab = "overview" | "revenue" | "students" | "tests";
type DateRange = "Today" | "7 Days" | "30 Days" | "This Year";

type Metric = {
  title: string;
  value: string;
  change: string;
  tone: "blue" | "green" | "orange" | "violet";
};

type ReportRow = {
  id: number;
  name: string;
  category: string;
  owner: string;
  generatedAt: string;
  size: string;
  status: "Ready" | "Processing" | "Failed";
};

const monthlyRevenue = [42, 56, 48, 72, 68, 84, 76, 94, 88, 110, 104, 126];
const monthlyStudents = [120, 180, 160, 240, 280, 310, 360, 420, 390, 470, 520, 610];
const testScores = [62, 68, 71, 66, 74, 78, 82, 79, 84, 88, 86, 91];

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const coursePerformance = [
  {
    course: "NEET Complete Preparation",
    students: 4250,
    revenue: 1845000,
    completion: 82,
    tests: 46,
  },
  {
    course: "JEE Main + Advanced",
    students: 3180,
    revenue: 1524000,
    completion: 76,
    tests: 38,
  },
  {
    course: "Class 8 Science Foundation",
    students: 2460,
    revenue: 638000,
    completion: 68,
    tests: 24,
  },
  {
    course: "Class 7 Maths Booster",
    students: 1890,
    revenue: 442000,
    completion: 61,
    tests: 18,
  },
];

const reportRows: ReportRow[] = [
  {
    id: 1,
    name: "Monthly Revenue Report",
    category: "Finance",
    owner: "Accounts Team",
    generatedAt: "30 May 2026, 10:40 AM",
    size: "2.4 MB",
    status: "Ready",
  },
  {
    id: 2,
    name: "Student Enrollment Analytics",
    category: "Students",
    owner: "Admin",
    generatedAt: "30 May 2026, 09:15 AM",
    size: "1.8 MB",
    status: "Ready",
  },
  {
    id: 3,
    name: "Test Performance Summary",
    category: "Exams",
    owner: "Academic Team",
    generatedAt: "30 May 2026, 08:55 AM",
    size: "Processing",
    status: "Processing",
  },
  {
    id: 4,
    name: "Pending Payment Follow-up",
    category: "Finance",
    owner: "Counsellor Team",
    generatedAt: "29 May 2026, 06:20 PM",
    size: "980 KB",
    status: "Ready",
  },
  {
    id: 5,
    name: "Live Class Attendance Report",
    category: "Classes",
    owner: "Teacher Panel",
    generatedAt: "29 May 2026, 03:30 PM",
    size: "Failed",
    status: "Failed",
  },
];

const heatmap = [
  [86, 74, 69, 92, 78, 88, 73],
  [72, 81, 76, 84, 90, 68, 77],
  [64, 70, 82, 79, 85, 91, 88],
  [93, 87, 76, 72, 68, 74, 80],
  [58, 66, 71, 75, 82, 86, 90],
];

const AdminReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>("overview");
  const [dateRange, setDateRange] = useState<DateRange>("30 Days");
  const [selectedCourse, setSelectedCourse] = useState("All Courses");

  const metrics = useMemo<Metric[]>(() => {
    if (activeTab === "revenue") {
      return [
        { title: "Total Revenue", value: "₹84.2L", change: "+24.1%", tone: "green" },
        { title: "Pending Amount", value: "₹2.8L", change: "-8.4%", tone: "orange" },
        { title: "Refunds", value: "₹48K", change: "+2.1%", tone: "violet" },
        { title: "Avg. Order Value", value: "₹4,860", change: "+11.2%", tone: "blue" },
      ];
    }

    if (activeTab === "students") {
      return [
        { title: "Total Students", value: "12,450", change: "+18.2%", tone: "blue" },
        { title: "New Admissions", value: "486", change: "+12.8%", tone: "green" },
        { title: "Drop-off", value: "3.4%", change: "-1.2%", tone: "orange" },
        { title: "Active Learners", value: "10,820", change: "+16.5%", tone: "violet" },
      ];
    }

    if (activeTab === "tests") {
      return [
        { title: "Tests Attempted", value: "8,420", change: "+32.5%", tone: "violet" },
        { title: "Avg. Score", value: "76%", change: "+6.8%", tone: "green" },
        { title: "Completion Rate", value: "84%", change: "+9.3%", tone: "blue" },
        { title: "Low Performers", value: "328", change: "-4.6%", tone: "orange" },
      ];
    }

    return [
      { title: "Total Revenue", value: "₹84.2L", change: "+24.1%", tone: "green" },
      { title: "Students", value: "12,450", change: "+18.2%", tone: "blue" },
      { title: "Test Attempts", value: "8,420", change: "+32.5%", tone: "violet" },
      { title: "Avg Attendance", value: "82%", change: "+7.4%", tone: "orange" },
    ];
  }, [activeTab]);

  const currentChartData =
    activeTab === "students"
      ? monthlyStudents
      : activeTab === "tests"
      ? testScores
      : monthlyRevenue;

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
      <section className="min-w-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[32px]">
        <div className="grid min-w-0 gap-0 2xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <div className="relative min-w-0 overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-5 text-white sm:p-8">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
            <div className="absolute -bottom-28 left-28 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

            <div className="relative min-w-0">
              <div className="mb-5 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                Reports & Analytics
              </div>

              <h1 className="max-w-3xl break-words text-2xl font-black tracking-tight sm:text-4xl 2xl:text-5xl">
                Smart reports for revenue, students and tests.
              </h1>

              <p className="mt-4 max-w-2xl text-sm font-semibold leading-6 text-slate-300 sm:text-base">
                Is page me admin daily/weekly/monthly reports dekh sakta hai,
                export kar sakta hai aur performance trend compare kar sakta hai.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <button className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">
                  Generate Report
                </button>

                <button className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
                  Schedule Email Report
                </button>
              </div>
            </div>
          </div>

          <div className="min-w-0 bg-slate-50 p-5 sm:p-8">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <div className="flex min-w-0 items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                    Report Snapshot
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">
                    May 2026
                  </h2>
                </div>

                <div className="shrink-0 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700">
                  Healthy
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <SnapshotCard title="Collected" value="₹18.4L" />
                <SnapshotCard title="Admissions" value="486" />
                <SnapshotCard title="Avg Score" value="76%" />
                <SnapshotCard title="Attendance" value="82%" />
              </div>

              <div className="mt-6 rounded-2xl bg-slate-950 p-4 text-white">
                <p className="text-xs font-bold text-slate-400">
                  Best performing course
                </p>
                <p className="mt-1 break-words text-sm font-black">
                  NEET Complete Preparation
                </p>
                <div className="mt-3 h-2 rounded-full bg-white/10">
                  <div className="h-2 w-[82%] rounded-full bg-cyan-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[28px] sm:p-5">
        <div className="flex min-w-0 flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { label: "Overview", value: "overview" },
              { label: "Revenue", value: "revenue" },
              { label: "Students", value: "students" },
              { label: "Tests", value: "tests" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value as ReportTab)}
                className={`rounded-2xl px-4 py-2.5 text-sm font-black transition ${
                  activeTab === tab.value
                    ? "bg-slate-950 text-white shadow-lg shadow-slate-950/10"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid min-w-0 gap-3 sm:grid-cols-[160px_minmax(220px,1fr)_auto] sm:items-center">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="h-11 min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-black text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
            >
              <option>Today</option>
              <option>7 Days</option>
              <option>30 Days</option>
              <option>This Year</option>
            </select>

            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="h-11 min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-black text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
            >
              <option>All Courses</option>
              <option>NEET Complete Preparation</option>
              <option>JEE Main + Advanced</option>
              <option>Class 8 Science Foundation</option>
              <option>Class 7 Maths Booster</option>
            </select>

            <button className="h-11 rounded-2xl bg-blue-600 px-5 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700">
              Export PDF
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <MetricCard key={metric.title} metric={metric} />
        ))}
      </section>

      <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-6">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[30px] sm:p-6">
            <div className="mb-6 flex min-w-0 flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <div className="min-w-0">
                <h2 className="break-words text-xl font-black text-slate-950">
                  {activeTab === "students"
                    ? "Student Enrollment Trend"
                    : activeTab === "tests"
                    ? "Average Test Score Trend"
                    : "Revenue Growth Trend"}
                </h2>
                <p className="mt-1 break-words text-sm font-semibold text-slate-500">
                  Showing data for {dateRange} • {selectedCourse}
                </p>
              </div>

              <div className="flex shrink-0 gap-2">
                <button className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white">
                  Monthly
                </button>
                <button className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-black text-slate-600">
                  Compare
                </button>
              </div>
            </div>

            <LineChart
              data={currentChartData}
              labels={months}
              activeTab={activeTab}
            />
          </div>

          <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(280px,0.8fr)_minmax(0,1.2fr)]">
            <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[30px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Conversion Funnel
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                Lead to paid student journey
              </p>

              <div className="mt-6 space-y-4">
                <FunnelRow
                  label="Leads"
                  value="18,420"
                  width="100%"
                  tone="bg-blue-600"
                />
                <FunnelRow
                  label="Counselling Done"
                  value="9,870"
                  width="74%"
                  tone="bg-cyan-500"
                />
                <FunnelRow
                  label="Trial Joined"
                  value="4,620"
                  width="52%"
                  tone="bg-violet-500"
                />
                <FunnelRow
                  label="Paid Students"
                  value="1,840"
                  width="34%"
                  tone="bg-emerald-500"
                />
              </div>
            </div>

            <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[30px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Attendance Heatmap
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                Batch-wise engagement view
              </p>

              <div className="mt-6 max-w-full overflow-x-auto">
                <div className="min-w-[520px] space-y-3">
                  {heatmap.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      className="grid grid-cols-8 items-center gap-2"
                    >
                      <p className="text-xs font-black text-slate-500">
                        Batch {rowIndex + 1}
                      </p>
                      {row.map((value, index) => (
                        <div
                          key={`${rowIndex}-${index}`}
                          className={`flex h-10 items-center justify-center rounded-xl text-xs font-black ${getHeatColor(
                            value
                          )}`}
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="grid grid-cols-8 gap-2 pl-[72px] text-center text-[11px] font-black text-slate-400">
                    <span>Mon</span>
                    <span>Tue</span>
                    <span>Wed</span>
                    <span>Thu</span>
                    <span>Fri</span>
                    <span>Sat</span>
                    <span>Sun</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[30px]">
            <div className="flex min-w-0 flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
              <div className="min-w-0">
                <h2 className="text-xl font-black text-slate-950">
                  Course Performance
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Revenue, students and completion by course
                </p>
              </div>

              <button className="w-fit shrink-0 rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200">
                Download CSV
              </button>
            </div>

            <div className="max-w-full overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                    <th className="px-5 py-4">Course</th>
                    <th className="px-5 py-4">Students</th>
                    <th className="px-5 py-4">Revenue</th>
                    <th className="px-5 py-4">Completion</th>
                    <th className="px-5 py-4">Tests</th>
                    <th className="px-5 py-4">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {coursePerformance.map((course) => (
                    <tr
                      key={course.course}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-5 py-4">
                        <p className="max-w-[240px] text-sm font-black text-slate-950">
                          {course.course}
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-400">
                          Active course
                        </p>
                      </td>

                      <td className="px-5 py-4 text-sm font-black text-slate-700">
                        {course.students.toLocaleString()}
                      </td>

                      <td className="px-5 py-4 text-sm font-black text-slate-700">
                        ₹{course.revenue.toLocaleString()}
                      </td>

                      <td className="px-5 py-4">
                        <div className="min-w-[120px]">
                          <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
                            <span>{course.completion}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100">
                            <div
                              className="h-2 rounded-full bg-blue-600"
                              style={{ width: `${course.completion}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm font-black text-slate-700">
                        {course.tests}
                      </td>

                      <td className="px-5 py-4">
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                          Good
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="min-w-0 space-y-6">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[30px]">
            <h2 className="text-xl font-black text-slate-950">Report Builder</h2>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              Custom report generate karo
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 2xl:grid-cols-1">
              <ReportOption
                title="Revenue Report"
                desc="Fees, refunds, pending payments"
                active
              />
              <ReportOption
                title="Student Report"
                desc="Admissions, active learners, drop-off"
              />
              <ReportOption
                title="Test Report"
                desc="Scores, attempts, rank and accuracy"
              />
              <ReportOption
                title="Class Report"
                desc="Attendance, recordings, engagement"
              />
            </div>

            <button className="mt-5 w-full rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700">
              Build Custom Report
            </button>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-950 p-5 text-white shadow-sm sm:rounded-[30px]">
            <h2 className="text-xl font-black">Target vs Achieved</h2>
            <p className="mt-1 text-sm font-semibold text-slate-400">
              Monthly business goal
            </p>

            <div className="mt-6 flex justify-center">
              <DonutChart percentage={78} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/10 p-4">
                <p className="text-xs font-bold text-slate-400">Target</p>
                <p className="mt-1 text-lg font-black">₹25L</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4">
                <p className="text-xs font-bold text-slate-400">Achieved</p>
                <p className="mt-1 text-lg font-black">₹19.5L</p>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[30px]">
            <div className="border-b border-slate-100 p-5">
              <h2 className="text-xl font-black text-slate-950">
                Generated Reports
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                Recent exports and files
              </p>
            </div>

            <div className="divide-y divide-slate-100">
              {reportRows.map((row) => (
                <div key={row.id} className="p-5">
                  <div className="flex min-w-0 items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-black text-slate-950">
                        {row.name}
                      </p>
                      <p className="mt-1 break-words text-xs font-bold text-slate-400">
                        {row.category} • {row.owner}
                      </p>
                      <p className="mt-1 break-words text-xs font-bold text-slate-400">
                        {row.generatedAt}
                      </p>
                    </div>

                    <ReportStatus status={row.status} />
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-xs font-black text-slate-400">
                      {row.size}
                    </span>
                    <button className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-200">
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

const MetricCard = ({ metric }: { metric: Metric }) => {
  const toneClass = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    orange: "bg-orange-50 text-orange-700 ring-orange-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
  }[metric.tone];

  return (
    <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl sm:rounded-[26px]">
      <div className="flex items-start justify-between gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg font-black ring-8 ${toneClass}`}
        >
          {metric.title.slice(0, 1)}
        </div>

        <span className="shrink-0 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-600">
          {metric.change}
        </span>
      </div>

      <p className="mt-6 text-sm font-bold text-slate-500">{metric.title}</p>
      <h2 className="mt-1 break-words text-3xl font-black tracking-tight text-slate-950">
        {metric.value}
      </h2>
    </div>
  );
};

const LineChart = ({
  data,
  labels,
  activeTab,
}: {
  data: number[];
  labels: string[];
  activeTab: ReportTab;
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = 100 - ((value - min) / (max - min || 1)) * 78 - 10;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="min-w-0 rounded-[24px] bg-slate-50 p-4 sm:rounded-[26px] sm:p-6">
      <div className="min-w-0 overflow-hidden rounded-[22px] bg-white p-4">
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="h-64 w-full"
        >
          {[20, 40, 60, 80].map((line) => (
            <line
              key={line}
              x1="0"
              y1={line}
              x2="100"
              y2={line}
              stroke="#e2e8f0"
              strokeWidth="0.4"
            />
          ))}

          <polyline
            points={points}
            fill="none"
            stroke="#2563eb"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          <polyline
            points={`0,100 ${points} 100,100`}
            fill="rgba(37,99,235,0.08)"
            stroke="none"
          />
        </svg>

        <div className="grid grid-cols-12 gap-1 text-center text-[10px] font-black text-slate-400 sm:text-[11px]">
          {labels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <ChartInfo
          label="Highest"
          value={activeTab === "revenue" ? `₹${max}K` : `${max}`}
        />
        <ChartInfo
          label="Lowest"
          value={activeTab === "revenue" ? `₹${min}K` : `${min}`}
        />
        <ChartInfo label="Growth" value="24.1%" />
      </div>
    </div>
  );
};

const ChartInfo = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0 rounded-2xl bg-white p-4">
    <p className="text-xs font-bold text-slate-400">{label}</p>
    <p className="mt-1 break-words text-lg font-black text-slate-950">
      {value}
    </p>
  </div>
);

const SnapshotCard = ({ title, value }: { title: string; value: string }) => (
  <div className="min-w-0 rounded-2xl bg-slate-50 p-4">
    <p className="text-xs font-bold text-slate-400">{title}</p>
    <p className="mt-1 break-words text-xl font-black text-slate-950">
      {value}
    </p>
  </div>
);

const FunnelRow = ({
  label,
  value,
  width,
  tone,
}: {
  label: string;
  value: string;
  width: string;
  tone: string;
}) => (
  <div className="min-w-0">
    <div className="mb-2 flex justify-between gap-3 text-sm font-black text-slate-700">
      <span>{label}</span>
      <span>{value}</span>
    </div>

    <div className="h-10 rounded-2xl bg-slate-100 p-1">
      <div
        className={`flex h-full min-w-[48px] items-center rounded-xl px-3 text-xs font-black text-white ${tone}`}
        style={{ width }}
      >
        {width}
      </div>
    </div>
  </div>
);

const DonutChart = ({ percentage }: { percentage: number }) => {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dash = (percentage / 100) * circumference;

  return (
    <div className="relative h-44 w-44 shrink-0">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="10"
        />

        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#22d3ee"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-3xl font-black text-white">{percentage}%</p>
        <p className="text-xs font-bold text-slate-400">Achieved</p>
      </div>
    </div>
  );
};

const ReportOption = ({
  title,
  desc,
  active = false,
}: {
  title: string;
  desc: string;
  active?: boolean;
}) => (
  <button
    className={`w-full min-w-0 rounded-2xl border p-4 text-left transition ${
      active
        ? "border-blue-200 bg-blue-50"
        : "border-slate-100 bg-slate-50 hover:border-blue-200 hover:bg-blue-50"
    }`}
  >
    <p className="break-words text-sm font-black text-slate-950">{title}</p>
    <p className="mt-1 break-words text-xs font-bold text-slate-500">{desc}</p>
  </button>
);

const ReportStatus = ({ status }: { status: ReportRow["status"] }) => (
  <span
    className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ${
      status === "Ready"
        ? "bg-emerald-50 text-emerald-700"
        : status === "Processing"
        ? "bg-blue-50 text-blue-700"
        : "bg-red-50 text-red-700"
    }`}
  >
    {status}
  </span>
);

const getHeatColor = (value: number) => {
  if (value >= 85) return "bg-emerald-100 text-emerald-800";
  if (value >= 75) return "bg-blue-100 text-blue-800";
  if (value >= 65) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
};

export default AdminReports;